import React from "react";
import PageHero from "@/components/PageHero";
import EnquiryForm from "@/components/EnquiryForm";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { SITE } from "@/lib/site";

const officeHours = [
 { day: "Mon – Fri", time: "10:00 AM – 7:00 PM" },
 { day: "Saturday", time: "Closed" },
 { day: "Sunday", time: "Closed" },
];

const ContactHeroSlot = () => (
 <div className="relative">
 <div className="absolute -top-5 -right-5 w-32 h-32 rounded-full bg-[#FDD805] opacity-90 -z-0" aria-hidden />
 <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-2xl border-2 border-[#0F1115] -z-0" aria-hidden />
 <div className="relative bg-white border border-[#EAEAEA] rounded-3xl p-7 shadow-[0_10px_40px_rgba(0,0,0,0.05)] z-10">
 <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">
 Office hours
 </div>
 <div className="mt-4 space-y-2.5">
 {officeHours.map((h) => (
 <div key={h.day} className="flex items-center justify-between gap-4 pb-2 border-b border-dashed border-[#EAEAEA] last:border-0">
 <div className="inline-flex items-center gap-2 text-[13px] font-bold text-[#0F1115]">
 <Clock size={13} className="text-[#FDD805]" /> {h.day}
 </div>
 <div className="text-[13px] text-[#4A4A4A] font-medium">{h.time}</div>
 </div>
 ))}
 </div>

 <div className="mt-5 pt-4 border-t border-[#EAEAEA]">
 <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A] mb-2">
 Visit us
 </div>
 <div className="text-[13px] text-[#0F1115] leading-relaxed flex items-start gap-2">
 <MapPin size={14} className="text-[#FDD805] mt-0.5 shrink-0" />
 <span className="whitespace-pre-line">{SITE.address}</span>
 </div>
 </div>
 </div>
 </div>
);

export default function ContactPage() {
 return (
 <div data-testid="page-contact">
 <Seo
  title="Contact Bhavik Hariyani | HG Corporates"
  description="Talk to Bhavik about Pvt Ltd, LLP, OPC, GST or DPIIT registration in Mumbai, Navi Mumbai and Thane. Message on WhatsApp or send an enquiry."
  path="/contact"
  breadcrumbs={[
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ]}
  jsonLd={{
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact HG Corporates",
    url: "https://hgcorporates.com/contact",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      telephone: "+91-9797193555",
      email: SITE.email,
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  }}
 />
 <PageHero
 eyebrow="Contact"
 title="Talk first. Decide with clarity."
 subtitle="Send us a message, or WhatsApp Bhavik directly. We reply within 24 hours."
 rightSlot={<ContactHeroSlot />}
 />

 <section className="bg-white">
 <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
 <Reveal className="lg:col-span-5">
 <div className="bg-[#F8F9FA] border border-[#EAEAEA] rounded-2xl p-8">
 <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#4A4A4A]">
 Reach us
 </div>
 <h2 className="mt-3 font-display text-[28px] font-black text-[#0F1115]">
 We're a WhatsApp away.
 </h2>

 <div className="mt-8 space-y-5">
 <a href={`mailto:${SITE.email}`} className="flex items-start gap-4" data-testid="contact-email">
 <div className="w-11 h-11 rounded-full bg-white border border-[#EAEAEA] flex items-center justify-center shrink-0">
 <Mail size={17} className="text-[#0F1115]" />
 </div>
 <div>
 <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">Email</div>
 <div className="text-[15px] font-bold text-[#0F1115]">{SITE.email}</div>
 </div>
 </a>
 <a href={`tel:${SITE.whatsappNumber}`} className="flex items-start gap-4" data-testid="contact-phone">
 <div className="w-11 h-11 rounded-full bg-white border border-[#EAEAEA] flex items-center justify-center shrink-0">
 <Phone size={17} className="text-[#0F1115]" />
 </div>
 <div>
 <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">Phone / WhatsApp</div>
 <div className="text-[15px] font-bold text-[#0F1115]">{SITE.phone}</div>
 </div>
 </a>
 <div className="flex items-start gap-4">
 <div className="w-11 h-11 rounded-full bg-white border border-[#EAEAEA] flex items-center justify-center shrink-0">
 <MapPin size={17} className="text-[#0F1115]" />
 </div>
 <div>
 <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#4A4A4A]">Office</div>
 <div className="text-[14px] font-medium text-[#0F1115] leading-relaxed whitespace-pre-line">{SITE.address}</div>
 </div>
 </div>
 </div>

 <a
 href={SITE.whatsappUrl}
 target="_blank"
 rel="noreferrer"
 className="mt-8 w-full bg-[#25D366] text-white rounded-full px-6 py-3.5 font-bold text-[15px] inline-flex items-center justify-center gap-2 hover:bg-[#1EBE57] hover:-translate-y-0.5 transition-all"
 data-testid="contact-whatsapp-cta"
 >
 <WhatsAppIcon size={18} /> Chat
 </a>
 </div>
 </Reveal>

 <Reveal className="lg:col-span-7" delay={120}>
 <EnquiryForm sourcePage="contact" />
 </Reveal>
 </div>
 </section>
 </div>
 );
}
